import { DAY_SECONDS, formatClock, type DispatchBoardMessage } from '@hotlist/shared';

/**
 * What your agency has to send somebody out for.
 *
 * DESIGN.md section 8. A camera that stops reporting has not said why, and from
 * a console a bag over the lens, a fault, a dead backhaul and a stolen unit look
 * identical. Somebody has to drive there. That drive is the cost, and the cost
 * is the point: an hour spent on somebody who covered a lens is an hour not
 * spent on the case the purchase was justified by.
 *
 * The ledger underneath counts and does not grade, exactly like the audit log.
 * There is no budget bar, no failure state and no efficiency score, because the
 * moment the game grades an agency's choices it has invented an oversight
 * mechanism the real arrangement does not have. The numbers go on the screen.
 * What they mean is the player's problem.
 */
export class DispatchPanel {
  private on = false;
  private readonly list: HTMLElement;
  private readonly ledger: HTMLElement;
  private readonly radarBtn: HTMLButtonElement;
  private readonly radarTitle: HTMLElement;
  private readonly radarDetail: HTMLElement;
  private daySeconds = DAY_SECONDS;

  constructor(onCheck: () => void, onGo: (camera: string) => void, onRadar: () => void) {
    this.list = must('dispatch-list');
    this.ledger = must('dispatch-ledger');
    this.radarBtn = must('kit-radar') as HTMLButtonElement;
    this.radarTitle = must('kit-radar-title');
    this.radarDetail = must('kit-radar-detail');
    (must('dispatch-refresh') as HTMLButtonElement).addEventListener('click', () => onCheck());
    this.radarBtn.addEventListener('click', () => onRadar());
    this.list.addEventListener('click', (event) => {
      const camera = (event.target as HTMLElement).dataset.camera;
      if (camera) onGo(camera);
    });
  }

  /** Reflect whether the car has a radar gun, and what the department can spend. */
  setKit(radar: boolean, price: number, deptRevenue: number): void {
    if (radar) {
      this.radarTitle.textContent = 'Radar gun';
      this.radarDetail.textContent = 'Fitted. A car you point it at reads its speed.';
      this.radarBtn.hidden = true;
    } else {
      const dept = Math.round(deptRevenue);
      this.radarTitle.textContent = 'Radar gun';
      this.radarDetail.textContent =
        `Clock a car's speed, so a stop for speeding holds up. ` +
        `Paid from department revenue ($${dept.toLocaleString()} available).`;
      this.radarBtn.hidden = false;
      this.radarBtn.textContent = `Purchase & equip — $${price.toLocaleString()}`;
      this.radarBtn.disabled = dept < price;
    }
  }

  /**
   * Marks this panel live. Visibility belongs to the console, which owns the
   * tabs: two things deciding whether one element is on screen is how a panel
   * ends up hidden behind the tab that is supposed to show it.
   */
  enable(): void {
    this.on = true;
  }

  setDaySeconds(seconds: number): void {
    if (Number.isFinite(seconds) && seconds > 0) this.daySeconds = seconds;
  }

  show(board: DispatchBoardMessage): void {
    this.list.replaceChildren();

    if (board.open.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'query-empty';
      empty.textContent = 'Everything is reporting.';
      this.list.append(empty);
    } else {
      for (const dispatch of board.open) {
        const row = document.createElement('div');
        row.className = 'dispatch-row';

        const when = document.createElement('span');
        when.className = 'dispatch-when';
        when.textContent = formatClock(dispatch.raised, this.daySeconds);

        const what = document.createElement('span');
        what.className = 'dispatch-what';
        // Deliberately says nothing about the cause, because nothing at a
        // console knows the cause. That is the whole mechanic.
        what.textContent = `${dispatch.camera} — silent, reason unknown`;

        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'dispatch-go';
        go.textContent = 'drive there';
        go.dataset.camera = dispatch.camera;

        row.append(when, what, go);
        this.list.append(row);
      }
    }

    const l = board.ledger;
    // Counted and reported, never graded. The renewal is decided on cases
    // closed -- section 8 is specific that the spend is justified by recovered
    // vehicles and cases closed -- and revenue is here because it is true, not
    // because it scores.
    this.ledger.textContent =
      `${board.agency}. ${l.casesClosed} case${l.casesClosed === 1 ? '' : 's'} closed, ` +
      `$${l.revenue.toLocaleString()} taken in fines and recoveries. ` +
      `${l.alerts} alerts, ${l.searches} searches, ` +
      `${l.stops} stops of which ${l.wrongfulStops} were on nobody. ` +
      `${l.dispatchesResolved} of ${l.dispatches} silent cameras looked at.`;
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
